import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import * as AuthenticationStore from 'store/redux/Authentication';
import { SetLoginStateAction, UserStateParam } from 'store/redux/Authentication';
import Context from 'configuration/context';
import EntraOidcClient from 'clients/EntraOidcClient';
import { telemetry } from 'services/telemery';
import { authenticationServiceFactory, IError } from 'services/authenticationService';

export type MSLoginState = 'IDLE' | 'ACQUIRING_MS_TOKEN' | 'DOING_FINAL_LOGIN';

interface ILoginState {
  state: MSLoginState;
}

const entraErrorCodes = ['access_denied', 'invalid_state', 'token_error'];

const useLogin = (
  setLoginState: ((state: UserStateParam) => SetLoginStateAction) | undefined,
  authenticationState: AuthenticationStore.UserState | undefined
) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [state, _setState] = React.useState<ILoginState>({
    state: 'IDLE',
  });
  const setState = (_state: ILoginState) => {
    console.debug(_state);
    _setState(_state);
  };
  const authenticationService = useMemo(() => {
    return authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState);
  }, [authenticationState, setLoginState]);
  var loginIsInProgress: boolean =
    authenticationState?.LoginState === AuthenticationStore.LoginState.LOGGING_IN;

  const onError = (error: IError) => {
    setState({
      state: 'IDLE',
    });
    console.error(error.errorCode, error.errorMessage);
    if (error.errorCode === '403') {
      telemetry.stopTrackEvent('Login process', { outcome: 'user not found' });
      setErrorMessage('login.ERROR_USER_NOT_FOUND');
    } else if (error.errorCode === '-1' && error.errorMessage === 'Could not find server') {
      telemetry.stopTrackEvent('Login process', { outcome: 'backend error' });
      setErrorMessage('login.ERROR_BACKEND_NOT_FOUND');
    } else if (entraErrorCodes.includes(error.errorCode)) {
      telemetry.stopTrackEvent('Login process', {
        outcome: 'microsoft login failed',
        'message:': error.errorMessage,
      });
      setErrorMessage('login.ERROR_MS_LOGIN_FAILED');
    } else {
      telemetry.stopTrackEvent('Login process', {
        outcome: 'unmanaged error',
        'message:': error.errorMessage,
      });
      setErrorMessage(error.errorMessage);
    }
  };

  const login = () => {
    try {
      telemetry.startTrackEvent('Login process');

      setState({
        state: 'ACQUIRING_MS_TOKEN',
      });

      authenticationService.beginMicrosoftLogin().catch(onError);
    } catch (error) {
      telemetry.stopTrackEvent('Login process', {
        outcome: 'unmanaged error',
        'error:': JSON.stringify(error),
      });
      Sentry.captureException(error);
      console.error('Unexpected error in "login"', error);
      setErrorMessage('login.ERROR_UNKNOWN_ERROR');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const authCode = params.get('code');
    const authState = params.get('state');
    const authError = params.get('error');

    if (!authState || !EntraOidcClient.hasState(authState)) return;

    telemetry.startTrackEvent('Login process');
    navigate('/login', { replace: true });

    if (authError) {
      EntraOidcClient.fromState(authState); // consume the single-use state key
      onError({
        errorCode: authError,
        errorMessage: params.get('error_description') ?? authError,
      });
      return;
    }

    if (!authCode) return;

    setState({
      state: 'DOING_FINAL_LOGIN',
    });
    authenticationService.completeMicrosoftLogin(authCode, authState).catch(onError);
  }, []);

  return {
    login,
    errorMessage,
    loginIsInProgress,
    state: state.state,
  };
};

export default useLogin;
